// Browser storage for data that belongs to ONE school. Every key is stored as `${schoolId}:<key>`,
// so a device used for several campuses never mixes their data, and switching campus needs no
// cleanup: the new campus simply reads its own keys.
//
// Per-device settings (colour theme, sidebar state, receipt size, last page...) are NOT school data;
// they keep using plain localStorage.
//
// The school defaults to window._schoolId, which the session loader (services/loadUser.js) sets
// from the signed-in user's active campus. With no school known, reads return null and writes do nothing.

const currentSchool = () => (typeof window !== 'undefined' ? window._schoolId || null : null);
const full = (schoolId, key) => `${schoolId}:${key}`;

export function getItem(key, schoolId = currentSchool()) {
  if (!schoolId) return null;
  try { return localStorage.getItem(full(schoolId, key)); } catch { return null; }
}

export function setItem(key, value, schoolId = currentSchool()) {
  if (!schoolId) return false;
  try { localStorage.setItem(full(schoolId, key), value); return true; } catch { return false; }
}

export function removeItem(key, schoolId = currentSchool()) {
  if (!schoolId) return;
  try { localStorage.removeItem(full(schoolId, key)); } catch { /* storage blocked */ }
}

// Before per-school storage, everything lived under plain keys, and only this school used the app.
// Its data is copied under the school's own prefix the first time that school loads. The plain keys
// are left in place as a backup, and nothing is copied for any other school.
export const LEGACY_SCHOOL_ID = 'f1ed8099-9fe6-4a3e-b114-8799974895c9';

const LEGACY_KEYS = [
  'appSettings', 'challanRecords', 'challanHistory',
  'advanceLedger', 'feeLedgerV2', 'feeLedgerV2_seq', 'advanceBalances', 'salesRecords',
  'savedPapers', 'dateSheets',
  'examLastScheduleCat', 'examLastMarkEntryCat', 'examLastResultsCat',
  'posReceiptCounters',
];

export function adoptLegacyKeys(schoolId) {
  if (schoolId !== LEGACY_SCHOOL_ID) return;
  try {
    LEGACY_KEYS.forEach((key) => {
      try {
        const old = localStorage.getItem(key);
        if (old === null) return;
        const target = full(schoolId, key);
        const current = localStorage.getItem(target);
        if (current === null || current === '') localStorage.setItem(target, old);
      } catch { /* skip this key */ }
    });
  } catch { /* storage blocked */ }
}

// Remove everything stored for one school (e.g. after a data reset).
export function clearSchool(schoolId = currentSchool()) {
  if (!schoolId) return;
  try {
    const prefix = `${schoolId}:`;
    Object.keys(localStorage).forEach(k => { if (k.startsWith(prefix)) localStorage.removeItem(k); });
  } catch { /* storage blocked */ }
}
